import { useEffect, useRef } from 'react';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { getEnvironmentQuery } from '@gonfalon/rest-api/queries/environments';
import { useQuery } from '@tanstack/react-query';

import { useOnboardingStorage } from '../../hooks';

/**
 * Hook that initializes onboarding storage with project and environment data.
 * Handles:
 * - Setting default project and environment from context on mount
 * - Fetching and syncing environment SDK keys when selection changes
 */
export function useInitializeOnboardingStorage() {
  const { selectedProject, selectedEnvironment, setOnboardingStorage } = useOnboardingStorage();
  const projectContext = useProjectContext();
  const { profile } = useAccountContext();

  const hasInitializedRef = useRef(false);
  const hasResetKeysForErrorRef = useRef<string | null>(null);

  const {
    data: environmentData,
    isPending: isEnvironmentDataPending,
    error: environmentError,
    isError: isEnvironmentError,
  } = useQuery({
    ...getEnvironmentQuery({
      projectKey: selectedProject?.key ?? '',
      environmentKey: selectedEnvironment?.key ?? '',
    }),
    enabled: !!selectedProject?.key && !!selectedEnvironment?.key,
  });

  // Set default values only once on mount
  useEffect(() => {
    if (!hasInitializedRef.current && projectContext?.project && projectContext?.selectedEnvironment && profile?._id) {
      hasInitializedRef.current = true;
      const { project, selectedEnvironment: env } = projectContext;
      setOnboardingStorage({
        selectedProject: { key: project.key, name: project.name },
        selectedEnvironment: { key: env.key, name: env.name, color: env.color },
        sdkInitKeys: {
          clientSide: env._id,
          mobile: env.mobileKey,
          serverSide: env.apiKey,
        },
        memberId: profile._id,
      });
    }
  }, [projectContext?.project, projectContext?.selectedEnvironment, profile?._id, setOnboardingStorage]);

  // Handle errors and update loading state
  useEffect(() => {
    if (isEnvironmentError && environmentError && selectedEnvironment) {
      const errorKey = `${selectedProject?.key}-${selectedEnvironment?.key}`;

      if (hasResetKeysForErrorRef.current !== errorKey) {
        hasResetKeysForErrorRef.current = errorKey;
        setOnboardingStorage({
          sdkInitKeys: {
            clientSide: 'client-key',
            mobile: 'mobile-key',
            serverSide: 'server-key',
          },
          isLoadingSdkKeys: false,
        });
      } else {
        setOnboardingStorage({ isLoadingSdkKeys: false });
      }
    } else if (!isEnvironmentError) {
      hasResetKeysForErrorRef.current = null;
      setOnboardingStorage({ isLoadingSdkKeys: isEnvironmentDataPending });
    }
  }, [
    isEnvironmentDataPending,
    isEnvironmentError,
    environmentError,
    selectedProject?.key,
    selectedEnvironment,
    setOnboardingStorage,
  ]);

  // Update SDK keys when environment selection changes
  useEffect(() => {
    if (
      selectedEnvironment &&
      !isEnvironmentError &&
      environmentData &&
      environmentData.key === selectedEnvironment.key
    ) {
      setOnboardingStorage({
        sdkInitKeys: {
          clientSide: environmentData._id,
          mobile: environmentData.mobileKey,
          serverSide: environmentData.apiKey,
        },
      });
    }
  }, [selectedEnvironment, environmentData, isEnvironmentError, setOnboardingStorage]);
}
